import { Link } from '@tanstack/react-router';
import { LogOut, SettingsIcon } from 'lucide-react';
import { NavSearchCommand } from '@/components/NavSearchCommand';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { isAdmin, normalizeRole } from '@/utils/roles';
import { useAuth } from '../contexts/AuthContext';

function getInitials(fullname: string | undefined, fallback: string): string {
  if (fullname?.trim()) {
    const parts = fullname.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return fullname.slice(0, 2).toUpperCase();
  }
  return fallback.slice(0, 2).toUpperCase() || 'U';
}

export function Header() {
  const { user, logout } = useAuth();
  const displayName = user?.name?.trim() || user?.userId || 'User';
  const initials = getInitials(user?.name, user?.userId ?? 'U');
  const userIsAdmin = isAdmin(normalizeRole(user?.role));

  return (
    <header className="fixed top-0 right-0 left-0 z-50 flex h-14 items-center gap-4 bg-primary px-6 text-primary-foreground">
      <Link
        to="/"
        className="flex shrink-0 items-center gap-3 text-primary-foreground no-underline"
      >
        <div className="flex size-8 items-center justify-center rounded bg-neutral-700 text-sm font-bold">
          ADM
        </div>
        <h1 className="text-lg font-semibold">Admin App</h1>
      </Link>

      <div className="mx-auto w-full max-w-sm flex-1 rounded-sm bg-white">
        <NavSearchCommand className="**:data-[slot=command-input-wrapper]:border-b-0" />
      </div>

      {userIsAdmin ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <Link
                to="/admin-setting"
                aria-label="Admin settings"
                className="flex size-8 shrink-0 items-center justify-center rounded-sm text-primary-foreground no-underline transition-colors hover:bg-white/10"
              />
            }
          >
            <SettingsIcon className="size-5" />
          </TooltipTrigger>
          <TooltipContent side="bottom">Admin settings</TooltipContent>
        </Tooltip>
      ) : null}

      <DropdownMenu>
        <DropdownMenuTrigger className="inline-flex shrink-0 rounded-full border-0 bg-transparent p-0 outline-none">
          <Avatar size="default" className="border border-brand-400">
            <AvatarFallback className="bg-primary text-primary-foreground">
              {initials}
            </AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-48">
          <DropdownMenuGroup>
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuLabel className="pt-0 font-normal text-muted-foreground">
              {displayName}
            </DropdownMenuLabel>
            {user?.email ? (
              <DropdownMenuLabel className="pt-0 font-normal text-muted-foreground">
                {user.email}
              </DropdownMenuLabel>
            ) : null}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => void logout()}>
              <LogOut data-icon="inline-start" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
